/*
 * PresetPanel — dropdown-style picker for saved chart presets plus save/
 * update/delete controls. Groups presets by scope (Mine / Gruppe / Anlage)
 * so an operator can spot at a glance which presets are shared.
 */

import { useState } from 'preact/hooks';
import type { ChartPresetDto } from '../../api/types';
import { showConfirm } from '../Confirmer';
import './PresetPanel.css';
import { ToolButton, ToolGroup } from '../ui/ToolButton';
import { IconCreate, IconDeleteHard, IconRename, IconSave } from '../../icons/HvacIcons';

interface Props {
  presets: ChartPresetDto[];
  loading: boolean;
  currentPresetId: string | null;
  currentUserId: string | null;
  hasUnsavedChanges: boolean;
  selectedLeafCount: number;
  onLoad(preset: ChartPresetDto): void;
  onSaveNew(name: string, scope: 'user' | 'plant'): Promise<void>;
  onUpdateCurrent(): Promise<void>;
  /** Rename or re-scope the currently loaded preset without touching its
   *  chart-payload. Called from the "Bearbeiten…" dialog. Body payload
   *  stays the same on the server side — the parent supplies the current
   *  leaf/settings JSON transparently. */
  onRenameCurrent(name: string, scope: 'user' | 'plant'): Promise<void>;
  onDelete(preset: ChartPresetDto): Promise<void>;
}

export function PresetPanel({
  presets,
  loading,
  currentPresetId,
  currentUserId,
  hasUnsavedChanges,
  selectedLeafCount,
  onLoad,
  onSaveNew,
  onUpdateCurrent,
  onRenameCurrent,
  onDelete,
}: Props) {
  const [saving, setSaving] = useState(false);
  // Ein Dialog fuer zwei Faelle: Save-as (neues Preset) und Bearbeiten
  // (Umbenennen + Sichtbarkeit aendern des bestehenden). Der Mode entscheidet
  // welcher onXxx aufgerufen wird und was der Header sagt. Vermeidet
  // zwei fast identische Modals.
  const [dialogMode, setDialogMode] = useState<null | 'save-as' | 'edit'>(null);
  const [pendingName, setPendingName] = useState('');
  const [pendingScope, setPendingScope] = useState<'user' | 'plant'>('user');

  const currentPreset = currentPresetId
    ? presets.find((p) => p.id === currentPresetId) ?? null
    : null;

  const canWriteCurrent = currentPreset
    ? (currentPreset.scope === 'User' ? currentPreset.ownerUserId === currentUserId : true)
    : false;

  const grouped = groupPresets(presets);

  return (
    <div class="hv-presets hv-presets--compact">
      <select
        class="hv-presets__select"
        value={currentPresetId ?? ''}
        disabled={loading || presets.length === 0}
        onChange={(e) => {
          const id = (e.currentTarget as HTMLSelectElement).value;
          const p = presets.find((x) => x.id === id);
          if (p) onLoad(p);
        }}
        aria-label="Preset laden"
        title="Preset laden"
      >
        <option value="">
          {loading
            ? 'Lade Presets…'
            : presets.length === 0
              ? '— keine Presets —'
              : '— Preset wählen —'}
        </option>
        {grouped.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.items.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <ToolGroup label="Preset">
        <ToolButton
          label={
            !currentPreset
              ? 'Speichern: zuerst ein Preset laden'
              : !canWriteCurrent
                ? 'Speichern: nur der Eigentümer kann dieses Preset überschreiben'
                : hasUnsavedChanges
                  ? 'Änderungen im Preset speichern'
                  : 'Gespeichert, keine Änderungen'
          }
          active={hasUnsavedChanges && !!currentPreset && canWriteCurrent}
          disabled={!currentPreset || !canWriteCurrent || !hasUnsavedChanges || saving}
          onClick={async () => {
            setSaving(true);
            try { await onUpdateCurrent(); }
            finally { setSaving(false); }
          }}
        >
          <IconSave size={18} />
        </ToolButton>
        <ToolButton
          label="Speichern als neues Preset"
          disabled={selectedLeafCount === 0 || saving}
          onClick={() => {
            setPendingName(currentPreset?.name ? `${currentPreset.name} (Kopie)` : '');
            setPendingScope('user');
            setDialogMode('save-as');
          }}
        >
          <IconCreate size={18} />
        </ToolButton>
        <ToolButton
          label="Preset umbenennen oder Sichtbarkeit ändern"
          disabled={!currentPreset || !canWriteCurrent || saving}
          onClick={() => {
            if (!currentPreset) return;
            setPendingName(currentPreset.name);
            // Backend kennt drei Scopes (user/plant/group). UI bietet aktuell
            // nur user/plant an — group folgt sobald die User-Membership im
            // /api/me-Payload liegt. Group-Presets bleiben deshalb im Edit-
            // Dialog auf ihrer Scope-Auswahl bis dahin (siehe Guard weiter unten).
            setPendingScope(currentPreset.scope === 'Plant' ? 'plant' : 'user');
            setDialogMode('edit');
          }}
        >
          <IconRename size={18} />
        </ToolButton>
        <ToolButton
          label="Preset löschen"
          tone="danger"
          disabled={!currentPreset || !canWriteCurrent || saving}
          onClick={async () => {
            if (!currentPreset) return;
            const ok = await showConfirm({
              title: 'Preset löschen?',
              message: `„${currentPreset.name}" wird entfernt. Andere Nutzer verlieren den Zugriff darauf.`,
              confirmLabel: 'Löschen',
              danger: true,
            });
            if (!ok) return;
            setSaving(true);
            try { await onDelete(currentPreset); }
            finally { setSaving(false); }
          }}
        >
          <IconDeleteHard size={18} />
        </ToolButton>
      </ToolGroup>

      {dialogMode !== null && (
        <div class="hv-presets__save-dialog" role="dialog" aria-modal="true">
          <div class="hv-presets__save-panel">
            <div class="hv-presets__save-title">
              {dialogMode === 'save-as' ? 'Preset speichern' : 'Preset bearbeiten'}
            </div>
            <label class="hv-presets__save-field">
              <span>Name</span>
              <input
                type="text"
                value={pendingName}
                onInput={(e) => setPendingName((e.currentTarget as HTMLInputElement).value)}
                placeholder="z.B. Kessel 1 – Vorlauf/Rücklauf"
                autoFocus
              />
            </label>
            <label class="hv-presets__save-field">
              <span>Sichtbarkeit</span>
              <select
                value={pendingScope}
                onChange={(e) => setPendingScope((e.currentTarget as HTMLSelectElement).value as 'user' | 'plant')}
              >
                <option value="user">Nur ich</option>
                <option value="plant">Ganze Anlage (alle Nutzer)</option>
              </select>
            </label>
            {dialogMode === 'edit' && currentPreset?.scope === 'Group' && (
              <div class="hv-presets__save-hint muted">
                Dieses Preset ist aktuell auf eine Gruppe zugewiesen. Änderungen
                über diesen Dialog schalten es auf „Nur ich" oder „Ganze Anlage"
                um; Gruppen-Zuordnung ist hier noch nicht editierbar.
              </div>
            )}
            <div class="hv-presets__save-actions">
              <button
                type="button"
                class="hv-btn hv-btn--sm"
                disabled={saving}
                onClick={() => setDialogMode(null)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                class="hv-btn hv-btn--sm hv-btn--primary"
                disabled={saving || pendingName.trim().length === 0}
                onClick={async () => {
                  setSaving(true);
                  try {
                    if (dialogMode === 'save-as') {
                      await onSaveNew(pendingName.trim(), pendingScope);
                    } else {
                      await onRenameCurrent(pendingName.trim(), pendingScope);
                    }
                    setDialogMode(null);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving
                  ? 'Speichere…'
                  : dialogMode === 'save-as' ? 'Speichern' : 'Übernehmen'}
              </button>
            </div>
          </div>
          <div class="hv-presets__save-backdrop" onClick={() => !saving && setDialogMode(null)} />
        </div>
      )}
    </div>
  );
}

interface Group { label: string; items: ChartPresetDto[] }

function groupPresets(presets: ChartPresetDto[]): Group[] {
  const groups: Group[] = [
    { label: 'Nur ich', items: [] },
    { label: 'Gruppe', items: [] },
    { label: 'Anlage', items: [] },
  ];
  for (const p of presets) {
    if (p.scope === 'User') groups[0].items.push(p);
    else if (p.scope === 'Group') groups[1].items.push(p);
    else if (p.scope === 'Plant') groups[2].items.push(p);
  }
  return groups.filter((g) => g.items.length > 0);
}
